import { useInfiniteQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import { getTab, STALE_TIME, type ApiParams, type Enveloped, type Page } from "../api";
import { apiParams, readShared } from "../filters";
import { frameContext, headData, needsMet, PageFrame, useFrameActions } from "../PageFrame";
import { usePublishPageMeta } from "../pageMeta";
import type { SourcingTab } from "../routes";
import { Footnote } from "./kit";
import { ALL_ROWS } from "./params";

/** At most this many 200-row calls per page view (1,000 rows). */
export const MAX_PAGES = 5;

export interface RowsData<Row> {
  rows: Row[];
  /** Rows C1 holds for this request, which can be more than `rows`. */
  total: number;
  /** True when the cap stopped the fetch before every row was held. */
  truncated: boolean;
}

/** The offset of the next call, or undefined when there is nothing more to ask for. */
export function nextOffset(held: number, total: number, lastCount: number, pageCount: number, maxPages = MAX_PAGES): number | undefined {
  if (lastCount === 0 || held >= total || pageCount >= maxPages) return undefined;
  return held;
}

/**
 * Every row of a tab, read in 200-row calls one after another. `enough` lets a
 * page stop early once it holds what it draws. A page drawn from this never
 * shows a half-loaded list: it stays in the loading state until the last call lands.
 */
export function useRows<Row>(tab: SourcingTab, extra: ApiParams = {}, enough?: (rows: Row[], total: number) => boolean) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const context = frameContext(search);
  const params = apiParams(tab, readShared(search), extra);
  const query = useInfiniteQuery({
    queryKey: ["sourcing_funnel", tab.apiTab, params, "rows"],
    queryFn: ({ pageParam }) => getTab<Page<Row>>(tab.apiTab, { ...params, limit: ALL_ROWS, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) =>
      nextOffset(
        pages.reduce((held, page) => held + (page.data?.items.length ?? 0), 0),
        last.data?.total ?? 0,
        last.data?.items.length ?? 0,
        pages.length,
      ),
    enabled: needsMet(tab, context),
    staleTime: STALE_TIME,
  });
  const pages = query.data?.pages ?? [];
  const first = pages[0];
  const rows = pages.flatMap((page) => page.data?.items ?? []);
  const total = pages[pages.length - 1]?.data?.total ?? 0;
  const satisfied = enough ? enough(rows, total) : false;
  const wantMore = query.hasNextPage && !satisfied;
  const { fetchNextPage, isFetchingNextPage, isFetchNextPageError } = query;

  useEffect(() => {
    if (wantMore && !isFetchingNextPage && !isFetchNextPageError) void fetchNextPage();
  }, [wantMore, isFetchingNextPage, isFetchNextPageError, fetchNextPage, pages.length]);

  const status: "pending" | "error" | "success" = isFetchNextPageError
    ? "error"
    : query.status === "success" && wantMore
      ? "pending"
      : query.status;
  const result: Enveloped<RowsData<Row>> | undefined = first
    ? {
        data: first.data === null ? null : { rows, total, truncated: !satisfied && rows.length < total },
        meta: first.meta,
      }
    : undefined;
  return { status, error: query.error, result, refetch: () => void query.refetch() };
}

/** `TabPage` for a page that draws every row: same states, same head rule. */
export function RowsPage<Row>({
  tab,
  areaName,
  extra,
  enough,
  isEmpty,
  head,
  children,
}: {
  tab: SourcingTab;
  areaName: string;
  extra?: ApiParams;
  enough?: (rows: Row[], total: number) => boolean;
  isEmpty?: (data: RowsData<Row>) => boolean;
  head?: (data: RowsData<Row> | null) => ReactNode;
  children: (data: RowsData<Row>) => ReactNode;
}) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const rows = useRows<Row>(tab, extra, enough);
  const actions = useFrameActions();
  const shared = frameContext(search);
  const empty = isEmpty ?? ((data: RowsData<Row>) => data.rows.length === 0);
  const shown = needsMet(tab, shared) ? headData(rows.status, rows.result, empty) : null;
  usePublishPageMeta(rows.status, rows.result?.meta);
  return (
    <>
      {head ? head(shown) : null}
      <PageFrame<RowsData<Row>>
        tab={tab}
        areaName={areaName}
        shared={shared}
        status={rows.status}
        error={rows.error}
        result={rows.result}
        isEmpty={empty}
        onRetry={rows.refetch}
        onClearFilters={actions.clearFilters}
        onOpenViewHealth={actions.openViewHealth}
      >
        {children}
      </PageFrame>
    </>
  );
}

/** Said under a per-option table that the row cap cut short. */
export function TruncatedNote({ shown }: { shown: number }) {
  return (
    <Footnote>
      Showing the first {shown} options by overall rating. Use the filters to narrow the list.
    </Footnote>
  );
}
